// Blueprint's navigation (DEC-008): the places across the top, each with its pages in the side rail by
// group (the register, rail C). Every office screen there is today sits in one place, so a company that
// switches to Blueprint (design.language) has all of them while each is rebuilt; who sees a page is still
// Nav.tsx's PLACES (by permission), never decided here. Blueprint's own pages (Needs you, Marketing's ads,
// Operations' gross profit) aren't in PLACES, so who sees each is BLUEPRINT_VIEWS's, by the actions it
// calls. Marketing and Operations are their own places since Brook's CRM notes (BL-136): a place with no
// page this person opens isn't drawn, so CSRs and technicians never see Operations.
import {
  AGENTS_PERMS,
  canSee,
  inOfficeApp,
  INVENTORY_PERMS,
  OFFICE_PERMS,
  PLACE_LINK,
  PLACES,
  type View,
} from '../Nav.tsx';
import { holdsAny } from '../shared/may.ts';
import { mayAds } from './ads-model.ts';
import { mayPerformance, ownScorecardOnly } from './performance-model.ts';
import { mayProfit } from './profit-model.ts';
import { seesHome } from './home-model.ts';
import { BOOKS_PARTS, isBooksPart, type BooksGroup } from '../books/parts.ts';
import { isSettingsPage, settingsPage, type SettingsPage } from '../settings/parts.ts';

export type PlaceId =
  'home' | 'schedule' | 'inbox' | 'customers' | 'accounting' | 'marketing' | 'operations' | 'setup';

export interface RailPage {
  view: View;
  label: string;
  /** A part of its screen with a link of its own (#/books/banking): Books' and Settings'. */
  part?: string;
  /** Who opens the part, by the permissions they hold, beyond who opens its screen (the screen's own check). */
  may?: (held: readonly string[]) => boolean;
  /** Other screens this page stands for, as tabs on it (Team manager: Time off, Hours worked). */
  covers?: readonly View[];
}

/** Books' parts in an Accounting rail group, Settings' in a Setup one, each with its own check. */
const booksIn = (g: BooksGroup): RailPage[] =>
  BOOKS_PARTS.filter((p) => p.group === g).map((p) => ({
    view: 'books',
    part: p.part,
    label: p.rail,
    ...(p.may ? { may: p.may } : {}),
  }));
const settingsIn = (...pages: SettingsPage[]): RailPage[] =>
  pages.map(settingsPage).map((p) => ({
    view: 'settings',
    part: p.page,
    label: p.label!,
    ...(p.may ? { may: p.may } : {}),
  }));
export interface BlueprintPlace {
  id: PlaceId;
  name: string;
  groups: { name: string; pages: RailPage[] }[];
  /** Always opens on its first page, even when a later one has work waiting (Schedule: the day board). */
  opensOnFirst?: true;
  /** Its pages with no rail link, opened from its pages (Book a visit, from the board's button). */
  offRail?: readonly RailPage[];
  /**
   * Named for its one group when that's all a person opens in it (QA-864): the warehouse opens only
   * Accounting's Stock, so its place is Stock; someone with only Your sign-in has You, not Setup.
   */
  namedForSoleGroup?: true;
}

export const BLUEPRINT_PLACES: readonly BlueprintPlace[] = [
  {
    id: 'home',
    name: 'Home',
    groups: [
      {
        name: 'Today',
        pages: [
          { view: 'home', label: 'Needs you' },
          { view: 'performance', label: 'Performance' },
          { view: 'huddle', label: 'The month so far' },
        ],
      },
    ],
  },
  {
    id: 'schedule',
    name: 'Schedule',
    opensOnFirst: true,
    // Amir, 26 Oct: the board, the map and the team; Book a visit is the board's button, and To
    // schedule a link on the board until its job table has a tab for it (BL-158)
    groups: [
      {
        name: 'Plan',
        pages: [
          { view: 'board', label: 'Dispatch board' },
          { view: 'map', label: 'Map' },
        ],
      },
      {
        name: 'Team',
        // until the team calendar is built (BL-159): who's working, time off and hours, as tabs
        pages: [{ view: 'team', label: 'Team manager', covers: ['timeoff', 'hours'] }],
      },
    ],
    offRail: [
      { view: 'book', label: 'Book a visit' },
      { view: 'tobook', label: 'To schedule' },
    ],
  },
  {
    id: 'inbox',
    name: 'Inbox',
    groups: [
      {
        name: 'Talk',
        pages: [
          { view: 'calls', label: 'Calls' },
          { view: 'messages', label: 'Texts and email' },
        ],
      },
      {
        name: 'Decide',
        pages: [
          { view: 'review', label: 'Jobs to review' },
          { view: 'approval', label: 'To approve' },
          { view: 'agents', label: 'Agents and approvals' },
        ],
      },
      {
        name: 'Follow up',
        pages: [
          { view: 'tasks', label: 'Tasks' },
          { view: 'notifications', label: 'Notifications' },
          { view: 'lists', label: 'Lists' },
        ],
      },
    ],
  },
  {
    id: 'customers',
    name: 'Customers',
    groups: [
      { name: 'Find', pages: [{ view: 'customers', label: 'All customers' }] },
      // the prototype's Keep: the members, and the equipment the company looks after
      {
        name: 'Keep',
        pages: [
          { view: 'memberships', label: 'Memberships' },
          { view: 'equipment', label: 'Equipment' },
        ],
      },
    ],
  },
  {
    id: 'accounting',
    name: 'Accounting',
    namedForSoleGroup: true,
    // the prototype's Accounting rail: Money in, Money out, Stock, Books; then the reports
    groups: [
      { name: 'Money in', pages: booksIn('Money in') },
      { name: 'Money out', pages: booksIn('Money out') },
      {
        name: 'Stock',
        pages: [
          { view: 'inventory', label: 'Stock' },
          { view: 'catalog', label: 'Catalog and prices' },
        ],
      },
      { name: 'Books', pages: booksIn('Books') },
      { name: 'Reports', pages: [{ view: 'reports', label: 'Reports' }] },
    ],
  },
  {
    id: 'marketing',
    name: 'Marketing',
    // AdTrack's pages (BL-136), once Home › Ads and calls: who sees marketing (marketing.view)
    groups: [
      {
        name: 'Ads and calls',
        pages: [
          { view: 'marketing', label: 'How the ads did' },
          { view: 'adcampaigns', label: 'Campaigns' },
          { view: 'adleads', label: 'Calls by source' },
        ],
      },
    ],
  },
  {
    id: 'operations',
    name: 'Operations',
    // the business's numbers behind job costing (BL-136), once Home › Profit: who holds
    // finance.margins.view (the owner, and whoever the owner gave margins to)
    groups: [
      {
        name: 'Profit',
        pages: [
          { view: 'operations', label: 'Gross profit' },
          { view: 'profitjobs', label: 'By job and technician' },
        ],
      },
    ],
  },
  {
    id: 'setup',
    name: 'Setup',
    namedForSoleGroup: true,
    // the setup audit (24 Oct): each page where its subject is. The price book first, in a group of its
    // own, so Setup opens on it (Amir, 26 Oct: more prominent in Setup, and a way in from Home)
    groups: [
      { name: 'What you sell', pages: settingsIn('pricebook') },
      {
        name: 'Your company',
        pages: [
          { view: 'company', label: 'Company details' },
          ...settingsIn('people', 'roles', 'agents', 'time'),
          { view: 'yourplan', label: 'Your plan and data' },
        ],
      },
      { name: 'Booking and jobs', pages: settingsIn('booking', 'org', 'forms', 'devices') },
      {
        name: 'Prices and money',
        pages: [
          ...settingsIn('plans', 'invoices', 'costs', 'tax'),
          ...booksIn('Setup'),
          ...settingsIn('payroll'),
        ],
      },
      { name: 'Customers and calls', pages: settingsIn('templates', 'reviews', 'calls') },
      {
        name: 'Connections',
        // the move off the system the company comes from, named for what it is, never the source
        pages: [...settingsIn('connections', 'campaigns'), { view: 'migration', label: 'Data import' }],
      },
      {
        name: 'You',
        pages: [
          { view: 'signin', label: 'Your sign-in' },
          { view: 'aitools', label: 'Your AI tools' },
        ],
      },
    ],
  },
];

/** A page with no rail link and no place of its own: what opens it, and its name. */
export interface RoutePage {
  /** Its key in Storybook coverage (packages/ui-web/coverage.ts). */
  key: string;
  /** Its route, or what it opens over. */
  opens: string;
  label: string;
}
/**
 * The office's pages that aren't in a place's rail: a page reached by its own route over the place it was
 * opened from (a job's page, a customer's record), and the sheets that open over a place (an invoice, the
 * estimate editor). They're pages of the site as the rail's are: Storybook coverage measures every page
 * here and in BLUEPRINT_PLACES, and a new one needs its entry (packages/ui-web/COVERAGE.md).
 */
export const BLUEPRINT_ROUTES: readonly RoutePage[] = [
  { key: 'job', opens: '#/jobs/<id>', label: 'Job page' },
  { key: 'customer', opens: '#/customers/<id>', label: 'A customer’s record' },
  { key: 'invoice', opens: '#/doc/invoice/<id>, over any place', label: 'Invoice sheet' },
  { key: 'estimate', opens: 'over the job page', label: 'Estimate editor' },
  { key: 'sellmembership', opens: 'over any place', label: 'Sell a membership' },
  { key: 'login', opens: 'before signing in, or when the session is lost', label: 'Sign-in page' },
];

/** Whether a rail page stands for a screen: its own, or one it covers (Team manager's tabs). */
const standsFor = (x: RailPage, view: View): boolean => x.view === view || !!x.covers?.includes(view);

/**
 * The place a screen is in: its part's own, where the part is in another place than its screen's first
 * (Books setup is in Setup, the rest of Books in Accounting). A page off the rail is its place's too.
 */
export function placeOf(view: View, part?: string | null): BlueprintPlace {
  const has = (p: BlueprintPlace, f: (x: RailPage) => boolean) =>
    p.groups.some((g) => g.pages.some(f)) || !!p.offRail?.some(f);
  return (
    (part ? BLUEPRINT_PLACES.find((p) => has(p, (x) => x.view === view && x.part === part)) : undefined) ??
    BLUEPRINT_PLACES.find((p) => has(p, (x) => standsFor(x, view)))!
  );
}

/** The page's own name in Blueprint's words: a part's own, for a screen with parts; a tab's, its page's. */
export function pageLabel(view: View, part?: string | null): string {
  for (const p of BLUEPRINT_PLACES)
    for (const x of [...p.groups.flatMap((g) => g.pages), ...(p.offRail ?? [])])
      if (standsFor(x, view) && (!part || !x.part || x.part === part)) return x.label;
  return view;
}

/**
 * Where a page is, as the nav says it: its place, then the page ("Setup › People", "Setup › Company
 * details"), for copy that sends someone there (QA-767), so it never names a path the nav has dropped.
 */
export const pagePath = (view: View, part?: string | null): string =>
  `${placeOf(view, part).name} › ${pageLabel(view, part)}`;

/** Whether a rail page is the one open: its screen or one it covers, and its part where it has one. */
export const isOpen = (x: RailPage, view: View, part?: string | null): boolean =>
  standsFor(x, view) && (!x.part || x.part === part);

/** Team manager's tabs, until the team calendar is built (BL-159). */
export const TEAM_TABS: readonly { view: View; label: string }[] = [
  { view: 'team', label: 'Who’s working' },
  { view: 'timeoff', label: 'Time off' },
  { view: 'hours', label: 'Hours worked' },
];

/** A part's own link (#/books/banking), when the part is one of its screen's. */
export const PART_LINK = /^#\/(books|settings)\/([a-z]+)$/;
export function linkedPart(hash: string): { view: 'books' | 'settings'; part: string } | null {
  const m = PART_LINK.exec(hash);
  if (!m) return null;
  const view = m[1] as 'books' | 'settings';
  const ok = view === 'books' ? isBooksPart(m[2]) : isSettingsPage(m[2]);
  return ok ? { view, part: m[2]! } : null;
}

const inOffice = (held: readonly string[]) => holdsAny(held, OFFICE_PERMS);

/** Who opens one of Blueprint's own pages: by the permissions the person holds (/auth/session). */
export type OpensPage = (held: readonly string[]) => boolean;

/**
 * The pages only Blueprint has, and who opens each: Needs you by its own sections, the ads by
 * marketing.view, profit by finance.margins.view itself and Performance by the permissions behind it
 * (P-0164, BL-137, QA-681: whichever of a person's roles gives it, a role of the company's own included).
 * Riverstone never draws them, so they're never in Nav's PLACES or its tabs.
 */
export const BLUEPRINT_VIEWS: Partial<Record<View, OpensPage>> = {
  home: seesHome,
  marketing: mayAds,
  adcampaigns: mayAds,
  adleads: mayAds,
  operations: mayProfit,
  profitjobs: mayProfit,
  // Home › Performance (BL-123): whoever holds a permission behind a part of it; a technician their own scorecard
  performance: mayPerformance,
  // Schedule's who's working and time off: the office, as the board (booking and cancelling time off is
  // the dispatchers' alone, inside the page)
  // the day's visits on the map: whoever opens the day board, whose answer it draws (dispatch.board)
  map: (held) => PLACES.some((p) => p.view === 'board' && canSee(p, held)),
  team: inOffice,
  timeoff: inOffice,
  // Inbox's To approve, for whoever decides agents' requests (approvals.decide), and Notifications, for
  // the office (the warehouse keeps the bell, with no Inbox to put a page in)
  approval: (held) => holdsAny(held, AGENTS_PERMS),
  notifications: inOffice,
  // Customers' Equipment: whoever opens a customer's record, where each piece is (customers.view)
  equipment: inOffice,
  // Stock's catalog: who keeps the stock, as Stock (pricebook.view to read; stock.setItem and the
  // price book's edits keep their own checks)
  catalog: (held) => holdsAny(held, INVENTORY_PERMS),
  // Setup's Your plan and data, the owner's as Company's two parts are (billing.manage, data.export: they
  // stay with the Owner role); Your AI tools, everyone's, as Your sign-in's part was
  yourplan: (held) => holdsAny(held, ['billing.manage', 'data.export']),
  aitools: () => true,
};

/** Whether a page is Blueprint's alone. */
export const blueprintOnly = (v: View): boolean => v in BLUEPRINT_VIEWS;

/**
 * Whether this person opens a page: Nav.tsx's PLACES and Blueprint's own pages (BLUEPRINT_VIEWS), both by
 * the permissions their session says they hold (/auth/session), never by role names (QA-680).
 */
export const canOpenIn =
  (held: readonly string[] = []) =>
  (v: View): boolean => {
    const own = BLUEPRINT_VIEWS[v];
    return own ? own(held) : PLACES.some((p) => p.view === v && canSee(p, held));
  };

/**
 * Whether the person is the office's at all (QA-680): they open one of Nav.tsx's places or, in Blueprint,
 * one of its own pages, by the permissions they hold whichever role gives them (a company's own role
 * included), never by role names. Your AI tools are everyone's and a technician's own scorecard is the
 * tech app's (BL-123), so neither makes someone the office's.
 */
export function opensOffice(held: readonly string[] = [], blueprint = false): boolean {
  if (inOfficeApp(held)) return true;
  if (!blueprint) return false;
  return Object.entries(BLUEPRINT_VIEWS).some(
    ([v, opens]) => v !== 'aitools' && !(v === 'performance' && ownScorecardOnly(held)) && !!opens?.(held),
  );
}

/** The Blueprint page a link opens (#/marketing), if it is one and this person opens it. */
export function blueprintLink(hash: string, held: readonly string[] = []): View | null {
  const v = PLACE_LINK.exec(hash)?.[1] as View | undefined;
  return v && blueprintOnly(v) && canOpenIn(held)(v) ? v : null;
}

/** Links to pages that moved (BL-136: Home's ads and profit), and Schedule's Week, now the board (26 Oct). */
export const MOVED_LINKS: Readonly<Record<string, View>> = {
  '#/ads': 'marketing',
  '#/profit': 'operations',
  '#/week': 'board',
};

/** The new address of an old link (#/ads is #/marketing now), or null when it didn't move. */
export const movedLink = (hash: string): string | null =>
  Object.hasOwn(MOVED_LINKS, hash) ? `#/${MOVED_LINKS[hash]!}` : null;

/**
 * The places and pages a person sees: those whose screens they may open (canOpen), a part only where
 * its own check lets what they hold open it, a place with none left out. Your sign-in is everyone's.
 * Its pages off the rail too, those they open.
 */
export function placesFor(canOpen: (v: View) => boolean, held: readonly string[] = []): BlueprintPlace[] {
  // a page that covers others opens on the first of them this person opens (Hours worked, for someone
  // who corrects hours but isn't the office's)
  const opened = (x: RailPage): RailPage | null => {
    const first = [x.view, ...(x.covers ?? [])].find(canOpen);
    return !first ? null : first === x.view ? x : { ...x, view: first };
  };
  return BLUEPRINT_PLACES.map((p) => {
    const groups = p.groups
      .map((g) => ({
        ...g,
        // a part needs its screen, and its own check (Payroll, Devices, Campaigns, Job forms)
        pages: g.pages.flatMap((x) => {
          if (x.view === 'signin') return [x];
          const o = opened(x);
          return o && (!o.may || o.may(held)) ? [o] : [];
        }),
      }))
      .filter((g) => g.pages.length);
    return {
      ...p,
      ...(p.namedForSoleGroup && groups.length === 1 ? { name: groups[0]!.name } : {}),
      groups,
      ...(p.offRail ? { offRail: p.offRail.filter((x) => canOpen(x.view)) } : {}),
    };
  }).filter((p) => p.groups.some((g) => g.pages.some((x) => x.view !== 'signin')) || p.id === 'setup');
}

/** The price book's page in Setup (Settings' part): where Home's way in goes (Amir, 26 Oct). */
export const PRICEBOOK_PAGE = { view: 'settings', part: 'pricebook' } as const satisfies Pick<
  RailPage,
  'view' | 'part'
>;

/**
 * Whether Home shows the way into the price book: the person reads it (pricebook.view), and Setup's rail
 * draws its page for them, by the rail's own check (placesFor), so Home never offers a page the rail hides.
 */
export function opensPricebook(held: readonly string[] = []): boolean {
  if (!held.includes('pricebook.view')) return false;
  return placesFor(canOpenIn(held), held).some((p) =>
    p.groups.some((g) =>
      g.pages.some((x) => x.view === PRICEBOOK_PAGE.view && x.part === PRICEBOOK_PAGE.part),
    ),
  );
}
